import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { DirectionsCar as VehicleIcon, Assignment as JobIcon } from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';

const libraries = ['places'];

const Map = () => {
  const [vehicles, setVehicles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState('');
  const [mapLoaded, setMapLoaded] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [markerType, setMarkerType] = useState('all'); // 'all', 'vehicles', 'jobs'
  const [geocodedLocations, setGeocodedLocations] = useState({});
  const geocoderRef = useRef(null);
  const mapRef = useRef(null);

  const mapContainerStyle = {
    width: '100%',
    height: 'calc(100vh - 200px)',
    minHeight: '600px',
  };

  const defaultCenter = {
    lat: 47.6062, // Seattle area (adjust to your service area)
    lng: -122.3321,
  };

  useEffect(() => {
    fetchData();
    // Refresh vehicle locations every 2 seconds to get updated GPS positions from drivers
    const interval = setInterval(() => {
      fetchData();
    }, 2000); // 2 seconds

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (mapLoaded && jobs.length > 0 && window.google) {
      if (!geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }
      geocodeJobLocations(jobs);
    }
  }, [mapLoaded, jobs]);

  const fetchData = async () => {
    try {
      setError('');
      setDataLoading(true);

      // Fetch vehicles and jobs in parallel
      const [vehiclesResponse, jobsResponse] = await Promise.all([
        api.get('/vehicles/'),
        api.get('/managers/jobs/'),
      ]);

      const vehiclesData = Array.isArray(vehiclesResponse.data)
        ? vehiclesResponse.data
        : vehiclesResponse.data?.results || [];

      const jobsData = Array.isArray(jobsResponse.data)
        ? jobsResponse.data
        : [];

      setVehicles(vehiclesData);
      setJobs(jobsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load map data');
    } finally {
      setDataLoading(false);
    }
  };

  const geocodeJobLocations = async (jobsData) => {
    if (!geocoderRef.current || !window.google) return;

    const geocoder = geocoderRef.current;
    const locations = {};

    for (const job of jobsData) {
      if (job.job_location && !geocodedLocations[job.id]) {
        try {
          const result = await new Promise((resolve, reject) => {
            geocoder.geocode({ address: job.job_location }, (results, status) => {
              if (status === 'OK' && results[0]) {
                resolve(results[0].geometry.location);
              } else {
                reject(status);
              }
            });
          });

          locations[job.id] = {
            lat: result.lat(),
            lng: result.lng(),
          };
        } catch (err) {
          console.error(`Failed to geocode ${job.job_location}:`, err);
        }
      }
    }

    if (Object.keys(locations).length > 0) {
      setGeocodedLocations((prev) => ({ ...prev, ...locations }));
    }
  };

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (window.google) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
    setMapLoaded(true);
  }, []);

  const getVehicleLocation = (vehicle) => {
    // First priority: Use vehicle's GPS coordinates (updated by driver)
    if (vehicle.latitude != null && vehicle.longitude != null) {
      return {
        lat: parseFloat(vehicle.latitude),
        lng: parseFloat(vehicle.longitude),
      };
    }
    // Fallback: If vehicle has a current job, use the job location
    if (vehicle.current_job) {
      const job = jobs.find((j) => j.id === vehicle.current_job);
      if (job && geocodedLocations[job.id]) {
        return geocodedLocations[job.id];
      }
    }
    // No location available
    return null;
  };

  const getFilteredMarkers = () => {
    const markers = [];

    if (markerType === 'all' || markerType === 'vehicles') {
      vehicles.forEach((vehicle) => {
        const location = getVehicleLocation(vehicle);
        if (location) {
          markers.push({
            id: `vehicle-${vehicle.id}`,
            type: 'vehicle',
            position: location,
            data: vehicle,
            title: `${vehicle.make} ${vehicle.model} (${vehicle.license_plate})`,
          });
        }
      });
    }

    if (markerType === 'all' || markerType === 'jobs') {
      jobs.forEach((job) => {
        if (geocodedLocations[job.id]) {
          markers.push({
            id: `job-${job.id}`,
            type: 'job',
            position: geocodedLocations[job.id],
            data: job,
            title: `Job ${job.id.substring(0, 8)} - ${job.job_location}`,
          });
        }
      });
    }

    return markers;
  };

  const getMarkerIcon = (type) => {
    if (!window.google || !window.google.maps) return null;
    
    if (type === 'vehicle') {
      // Truck icon - top view: cab (smaller front) + cargo area (larger back)
      // Simple rectangular shapes that form a truck
      return {
        path: 'M-20,-8 L-5,-8 L-5,-4 L5,-4 L5,-8 L20,-8 L20,8 L-20,8 Z M-15,-3 L-8,-3 L-8,1 L-15,1 Z M8,-3 L15,-3 L15,3 L8,3 Z',
        fillColor: '#1E3A5F',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 1,
        anchor: new window.google.maps.Point(0, 0),
      };
    } else {
      return {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: '#4A90E2',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      };
    }
  };

  const markers = getFilteredMarkers();

  return (
    <Layout>
      <Container>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Map View
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            View all vehicles and jobs on the map
          </Typography>

          <ToggleButtonGroup
            value={markerType}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) setMarkerType(newValue);
            }}
            aria-label="marker type"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="all" aria-label="all">
              All
            </ToggleButton>
            <ToggleButton value="vehicles" aria-label="vehicles">
              Vehicles
            </ToggleButton>
            <ToggleButton value="jobs" aria-label="jobs">
              Jobs
            </ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Chip
              icon={<VehicleIcon />}
              label={`${vehicles.length} Vehicles`}
              color="primary"
              variant="outlined"
            />
            <Chip
              icon={<JobIcon />}
              label={`${jobs.length} Jobs`}
              color="secondary"
              variant="outlined"
            />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY && !'AIzaSyCNRC6IxWlZZkx8HzTBctZydYIAchJUp9E' ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Google Maps API key is not configured. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your environment variables.
          </Alert>
        ) : null}

        {scriptError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load Google Maps. Please check your API key and ensure the Maps JavaScript API is enabled.
          </Alert>
        ) : null}

        <Paper elevation={3} sx={{ overflow: 'hidden', position: 'relative' }}>
          {!scriptLoaded && !scriptError && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                zIndex: 1000,
              }}
            >
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading Google Maps...
              </Typography>
            </Box>
          )}
          <LoadScript
            googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyCNRC6IxWlZZkx8HzTBctZydYIAchJUp9E'}
            libraries={libraries}
            onLoad={() => {
              setScriptLoaded(true);
            }}
            onError={() => {
              setScriptError(true);
              setError('Failed to load Google Maps script. Please check your API key.');
            }}
          >
            {scriptError ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" p={3}>
                <Alert severity="error">
                  Failed to load Google Maps. Please check your API key and ensure the Maps JavaScript API is enabled in Google Cloud Console.
                </Alert>
              </Box>
            ) : scriptLoaded ? (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={defaultCenter}
                zoom={10}
                onLoad={onMapLoad}
                options={{
                  disableDefaultUI: false,
                  zoomControl: true,
                  streetViewControl: false,
                  mapTypeControl: true,
                  fullscreenControl: true,
                }}
              >
                {markers.map((marker) => (
                  <Marker
                    key={marker.id}
                    position={marker.position}
                    title={marker.title}
                    icon={getMarkerIcon(marker.type)}
                    onClick={() => setSelectedMarker(marker)}
                  />
                ))}

                {selectedMarker && (
                  <InfoWindow
                    position={selectedMarker.position}
                    onCloseClick={() => setSelectedMarker(null)}
                  >
                    <Box sx={{ p: 1, maxWidth: 250 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        {selectedMarker.type === 'vehicle' ? (
                          <>
                            <VehicleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                            Vehicle
                          </>
                        ) : (
                          <>
                            <JobIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                            Job
                          </>
                        )}
                      </Typography>
                      {selectedMarker.type === 'vehicle' ? (
                        <>
                          <Typography variant="body2">
                            <strong>License:</strong> {selectedMarker.data.license_plate}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Make/Model:</strong> {selectedMarker.data.make}{' '}
                            {selectedMarker.data.model}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Status:</strong> {selectedMarker.data.status}
                          </Typography>
                          {selectedMarker.data.driver_name && (
                            <Typography variant="body2">
                              <strong>Driver:</strong> {selectedMarker.data.driver_name}
                            </Typography>
                          )}
                          {selectedMarker.data.latitude != null && selectedMarker.data.longitude != null && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              <strong>Location:</strong> {parseFloat(selectedMarker.data.latitude).toFixed(6)}, {parseFloat(selectedMarker.data.longitude).toFixed(6)}
                            </Typography>
                          )}
                          {selectedMarker.data.location_updated_at && (
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
                              Updated: {new Date(selectedMarker.data.location_updated_at).toLocaleString()}
                            </Typography>
                          )}
                          {selectedMarker.data.current_job && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              <strong>Current Job:</strong> {selectedMarker.data.current_job.substring(0, 8)}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <>
                          <Typography variant="body2">
                            <strong>Job ID:</strong> {selectedMarker.data.id.substring(0, 8)}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Location:</strong> {selectedMarker.data.job_location}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Status:</strong> {selectedMarker.data.job_status}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Customer:</strong> {selectedMarker.data.customer_name}
                          </Typography>
                          {selectedMarker.data.driver_name && (
                            <Typography variant="body2">
                              <strong>Driver:</strong> {selectedMarker.data.driver_name}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </InfoWindow>
                )}
              </GoogleMap>
            ) : null}
          </LoadScript>
        </Paper>
      </Container>
    </Layout>
  );
};

export default Map;

